import { useEffect, useRef } from "react";
import { ScheduleTemplateListItem } from "@common/schemas/domains/accounting/scheduleTemplate.schema";
import { resolveSplitScheduleHolding } from "./resolveSplitScheduleHolding";

interface SplitNeedingScheduleAccrual {
    scheduleTemplateId?: string | null;
    accrualAccountId?: string | null;
    entityId?: number;
}

export function useFillMissingSplitScheduleAccrual<
    T extends SplitNeedingScheduleAccrual,
>(params: {
    splits: T[];
    scheduleTemplates: ScheduleTemplateListItem[];
    workspaceId: string | null | undefined;
    onSplitsFilled: (splits: T[]) => void;
}): void {
    const attemptedFillKeysRef = useRef(new Set<string>());
    const { splits, scheduleTemplates, workspaceId, onSplitsFilled } = params;

    useEffect(() => {
        if (splits.length === 0 || !workspaceId) {
            return;
        }
        if (
            !splits.some(
                (split) => split.scheduleTemplateId && !split.accrualAccountId,
            )
        ) {
            return;
        }

        let cancelled = false;
        void (async () => {
            const nextSplits = [...splits];
            let didChange = false;
            for (const [index, split] of nextSplits.entries()) {
                if (!split.scheduleTemplateId || split.accrualAccountId) {
                    continue;
                }
                const fillKey = `${split.scheduleTemplateId}:${index}`;
                if (attemptedFillKeysRef.current.has(fillKey)) {
                    continue;
                }
                const selectedTemplate = scheduleTemplates.find(
                    (template) => template.id === split.scheduleTemplateId,
                );
                if (!selectedTemplate) {
                    continue;
                }
                try {
                    const patch = await resolveSplitScheduleHolding({
                        template: selectedTemplate,
                        entityId: split.entityId,
                        workspaceId,
                    });
                    if (cancelled || !patch.accrualAccountId) {
                        continue;
                    }
                    attemptedFillKeysRef.current.add(fillKey);
                    nextSplits[index] = {
                        ...split,
                        accrualAccountId: patch.accrualAccountId,
                        entityId: patch.entityId ?? split.entityId,
                    };
                    didChange = true;
                } catch {
                    continue;
                }
            }
            if (cancelled || !didChange) {
                return;
            }
            onSplitsFilled(nextSplits);
        })();

        return () => {
            cancelled = true;
        };
    }, [onSplitsFilled, scheduleTemplates, splits, workspaceId]);
}
